import { Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import { CustomRouteMobileComponent } from "./components/custom-route-mobile/custom-route-mobile.component";
import { CustomRouteDesktopComponent } from './components/custom-route-desktop/custom-route-desktop.component';
import { CustomRouteService } from './services/custom-route.service';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { CustomRoute } from './interfaces/custom-route.interface';
import { filter, catchError, EMPTY, tap,  first, expand, map, of, switchMap, throwError } from 'rxjs';
import { CustomRouteResponseCompleted, isCustomRouteResponseCompleted } from './interfaces/custom-route.response';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RegisterUserService } from '../register-user/services/register-user.service';
import { PlannerDayStateService, PlannerViewsStateService } from './shared/planner/services';
import { RouteDataService } from './shared/map/services/route-data.service';
import { BusLoaderComponent } from "../register-user/shared/components/bus-loader/bus-loader.component";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogComponent } from 'src/app/shared/dialogs/dialog/dialog.component';
import { MatDialogService } from 'src/app/services/mat-dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { GoogleTranslateService } from 'src/app/services/google-translate.service';
import { Location } from '@angular/common';
import { RegisterUserForm } from '../register-user/interfaces';

@Component({
  selector: 'app-custom-route',
  standalone: true,
  templateUrl: './custom-route.component.html',
  styleUrls: ['./custom-route.component.scss'],
  imports: [
    TranslateModule,
    CustomRouteMobileComponent,
    CustomRouteDesktopComponent,
    BusLoaderComponent
],
})
export class CustomRouteComponent implements OnDestroy {
  private _translateService = inject(TranslateService);
  private _router = inject(Router);
  private _activedRoute = inject(ActivatedRoute);
  private _sessionStorageService = inject(SessionStorageService);
  private _customRouteService = inject(CustomRouteService);
  private _breakpointsService = inject(BreakpointsService);
  private _registerUserService = inject(RegisterUserService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _plannerViewsStateService = inject(PlannerViewsStateService);
  private _routeDataService = inject(RouteDataService);
  private _matDialogService = inject(MatDialogService);
  private _googleTranslateService = inject(GoogleTranslateService);
  private _location = inject(Location);
  private _destroyRef = inject(DestroyRef);

  protected customRouteData =  this._customRouteService.customRouteData;
  private _numberOfDaysCustomRoute = this._registerUserService.numberOfDaysCustomRoute;
  private  _currentOrder = this._customRouteService.currentOrder;
  protected isLoading = this._customRouteService.isLoading;
 
  constructor() { 
    const routeToken = this._activedRoute.snapshot.paramMap.get('id');
    const lang = this._activedRoute.snapshot.paramMap.get('lang');

    if (lang && routeToken){
      this._location.replaceState(`/custom-route/${routeToken}`);
      this._googleTranslateService.removeLocalLang();
      this._googleTranslateService.changeLangOnInit(lang);
    }

    if(routeToken) {
      this._loadCustomRouteFromRouteToken(routeToken);
    } else {
      this._redirectToPlannerView();
      
      this._initializeStateFromSession();
      this._startPollingIfNeeded();
      
    }
    
    // Get the welcome view state from session storage
    const welcomeViewStateSessionStorage = this._sessionStorageService.getItem('customRouteWelcomeView') as boolean;
    this._customRouteService.setIsWelcomeViewShown(welcomeViewStateSessionStorage);
    
    console.log('CUSTOM ROUTE DATA:', this.customRouteData());
  }

   /**
   * Inicializa señales desde sessionStorage (solo una vez al montar).
   */
  private _initializeStateFromSession(): void {
    const storedRegisterUserForm = this._sessionStorageService.getItem<RegisterUserForm>('registerUserForm');
    if (storedRegisterUserForm) {
      this._registerUserService.setRegisterUserFormData(storedRegisterUserForm);
    }
    
    // const numberOfDaysCustomRoute = this._sessionStorageService.getItem<number>('numberOfDaysCustomRoute');
    // if (numberOfDaysCustomRoute !== null && numberOfDaysCustomRoute !== undefined) {
    //   this._registerUserService.numberOfDaysCustomRoute.set(numberOfDaysCustomRoute);
    // }
    
    const storedDayToShowIndex = this._sessionStorageService.getItem<number>('dayToShowIndex');
    if (storedDayToShowIndex !== null && storedDayToShowIndex !== undefined) {
      this._plannerDayStateService.setDayToShowIndex(storedDayToShowIndex);
    }

    const storedCustomRouteData = this._sessionStorageService.getItem<CustomRoute>('customRouteData');
    if (storedCustomRouteData) {
      this._customRouteService.setCustomRouteData(storedCustomRouteData);
    }

    const numberOfDays = this._sessionStorageService.getItem<number>('numberOfDaysCustomRoute');
    if (numberOfDays) {
      this._registerUserService.setNumberOfDaysCustomRoute(numberOfDays);
    }
    
    // const test: CustomRoute = this._sessionStorageService.getTestData();
    // if (test)
    //   this._customRouteService.customRouteData.set(test);

    const storedToken = this._sessionStorageService.getItem<string>('customRouteToken');
    if (storedToken) {
      this._customRouteService.setToken(storedToken);
    }

    const storedCurrentOrder = this._sessionStorageService.getItem<number>('customRouteCurrentOrder');
    if (storedCurrentOrder) {
      this._customRouteService.setCurrentOrder(storedCurrentOrder);
    }

    const isGeolocationActive = this._sessionStorageService.getItem<CustomRoute>('geolocation');
    if (isGeolocationActive) {
      this._routeDataService.enableGeolocation();
    }

    const showDevPanel = this._sessionStorageService.getItem<CustomRoute>('showDevPanel');
    if (showDevPanel) {
      this._routeDataService.showDevPanel.set(true);
    }
  }

  /**
   * Lógica para cargar progresivamente los días restantes
   */
  private _fetchRemainingDaysSequentially(): void {
    const token = this._customRouteService.token();
    const numberOfDays = this._numberOfDaysCustomRoute();
    
    if (!token) {
      console.warn('No token available, cannot fetch additional days.');
      return;
    }

    if (this._currentOrder() >= numberOfDays) {
      console.log('🎉 Todos los días ya están completados.');
      return;
    }

    of(this._currentOrder()).pipe(
      expand((order) => {
        if (order >= numberOfDays) return EMPTY;

        return this._customRouteService.launchRoutePlanning(token, order).pipe(
          filter(isCustomRouteResponseCompleted),
          first(),
          switchMap(() => 
            this._customRouteService.getCustomRouteResponse(token, order).pipe(
              tap((res: CustomRouteResponseCompleted) => {
                console.log(`✅ Día ${order + 1} recibido`, res);
                this._customRouteService.addDailyRoute(res);
                this._customRouteService.setCurrentOrder(this._customRouteService.currentOrder() + 1);
                this._sessionStorageService.setItem<CustomRoute>('customRouteData', this._customRouteService.customRouteData());
                this._sessionStorageService.setItem<number>('customRouteCurrentOrder', this._customRouteService.currentOrder());
              }),
              map(() => order + 1)
            )
          ),
          catchError((error: HttpErrorResponse) => {
            console.error(`❌ Error procesando día ${order + 1}:`, error);
            return throwError(() => new Error('HTTP request failed', { cause: error })); // Re-throw the error to stop the polling
          })
        );
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ Error en polling secuencial:', error);
        this._openServerErrorDialog();
        return throwError(() => new Error('HTTP request failed', { cause: error }));
      }),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe({
      complete: () => {
        console.log('🎉 Todos los días completados.');
      }
    });
  }

  /**
   * Inicia el polling solo si ya se recibió al menos un día.
   */
  private _startPollingIfNeeded(): void {
    if (this.customRouteData()?.dailyRoutes?.length >= 1) {
      this._fetchRemainingDaysSequentially();
    }
  }
  
  /**
   * Carga la ruta personalizada desde el token de la ruta.
   * Para rutas cargadas desde el email, se espera que el token esté en la URL
   */
  private _loadCustomRouteFromRouteToken(routeToken: string) {
    this._customRouteService.getCustomRouteResponse(routeToken).subscribe((res: any) => {
      this._customRouteService.setToken(res.token);
      this._plannerDayStateService.setDayToShowIndex(0);
      this._customRouteService.setCurrentOrder(res.dailyRoutes?.length);
      this._registerUserService.setNumberOfDaysCustomRoute(res.dailyRoutes?.length);


      // add all the daily routes to the customRouteData
      res.dailyRoutes?.forEach((_, index) => {
        const args: CustomRouteResponseCompleted = {
          status: 200,
          id: res.id,
          token: res.token,
          dailyRoutes: res.dailyRoutes[index]
        }
        this._customRouteService.addDailyRoute(args); 
      })
   
      this._customRouteService.setCustomRouteData(this.customRouteData());
      const showDevPanel = this._sessionStorageService.getItem<CustomRoute>('showDevPanel');
      if (showDevPanel) {
        this._routeDataService.showDevPanel.set(true);
      }
    })
  }
  
  private _openServerErrorDialog(): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: DialogComponent,
      data: {
        iconName: 'alert',
        title: this._translateService.instant('Error de servidor'),
        content: this._translateService.instant('Ha ocurrido un error') + '. ' + this._translateService.instant('Inténtelo de nuevo más tarde'),
        primaryActionButtonText: this._translateService.instant('Aceptar'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
          this._router.navigate(['/register-user']);
        }
      }
    })
  }
  
  public isMobile() {
    return this._breakpointsService.isMobile();
  }
  
  private _redirectToPlannerView() {
    this._plannerViewsStateService.isEditRouteView.set(false);
  }
  
  ngOnDestroy(): void {
    this._sessionStorageService.removeAll();
  }
}
